import { useEffect, useState, useCallback } from "react";
import { CheckCircle2, Layers, Trash2, Users, X } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loading } from "@/components/bits";

/** Daftar marking yang sudah dipindai dalam Mode Massal, menunggu kirim OK sekaligus */
export function BulkQueue({ items, onRemove, onClear, onSubmit, busy }) {
  const ready = items.filter((i) => !i.status || i.status === "error");
  return (
    <div className="mt-4 rounded border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="qc-bulk-panel">
      <div className="flex items-center gap-2 text-xs text-emerald-300">
        <Layers className="h-4 w-4" /> Mode Massal — scan beberapa label berturut-turut, lalu kirim OK sekaligus
        <span className="ml-auto font-mono text-slate-400" data-testid="qc-bulk-count">{items.length} marking</span>
      </div>
      {items.length ? (
        <ul className="mt-2 max-h-56 space-y-1 overflow-y-auto" data-testid="qc-bulk-list">
          {items.map((it) => (
            <li key={it.code} className="flex items-center gap-2 rounded bg-slate-900/70 px-2 py-1.5 text-xs" data-testid={`qc-bulk-item-${it.code}`}>
              <span className="font-mono font-bold text-amber-400">{it.code}</span>
              <span className="truncate text-slate-400">{it.projectName}</span>
              <span className="font-mono text-slate-500">QC {it.qcOk}/{it.qty}</span>
              {it.status === "ok" && <span className="ml-auto inline-flex items-center gap-1 text-emerald-400"><CheckCircle2 className="h-3 w-3" />OK</span>}
              {it.status === "error" && <span className="ml-auto text-rose-400" title={it.detail}>{it.detail}</span>}
              {!it.status && <button type="button" onClick={() => onRemove(it.code)} className="ml-auto text-slate-600 hover:text-rose-400" data-testid={`qc-bulk-remove-${it.code}`}><X className="h-3.5 w-3.5" /></button>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-slate-500" data-testid="qc-bulk-empty">Belum ada marking dalam antrean. Scan label untuk menambahkan.</p>
      )}
      <div className="mt-3 flex gap-2">
        <Button type="button" disabled={busy || !ready.length} onClick={onSubmit} className="flex-1 bg-emerald-600 hover:bg-emerald-500" data-testid="qc-bulk-submit">
          <CheckCircle2 className="mr-2 h-4 w-4" />Kirim Semua OK ({ready.length}) / 全部合格
        </Button>
        <Button type="button" variant="outline" disabled={busy || !items.length} onClick={onClear} className="border-slate-700" data-testid="qc-bulk-clear">
          <Trash2 className="mr-2 h-4 w-4" />Kosongkan
        </Button>
      </div>
    </div>
  );
}

/** Jumlah scan QC per operator — hari ini atau 7 hari terakhir (per hari) */
export function OperatorStats({ refreshKey }) {
  const [stats, setStats] = useState(null);
  const [range, setRange] = useState("day");
  const load = useCallback(() => {
    api.get(`/scans/stats?days=${range === "week" ? 7 : 1}`).then((r) => setStats(r.data)).catch(() => setStats({ operators: [], total: 0, days: [] }));
  }, [range]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const max = Math.max(1, ...(stats?.operators || []).map((o) => o.total));
  const dayMax = Math.max(1, ...(stats?.operators || []).flatMap((o) => o.daily || []));
  const dayLabel = (d) => new Date(d + "T00:00:00").toLocaleDateString("id-ID", { weekday: "short", day: "2-digit" });
  return (
    <div className="panel p-4" data-testid="qc-operator-stats">
      <h3 className="mb-2 flex flex-wrap items-center gap-2 font-display text-sm font-bold text-slate-200">
        <Users className="h-4 w-4 text-sky-400" /> Scan per Operator <span className="cn text-[11px] font-normal text-slate-500">操作员扫描</span>
        <div className="ml-auto inline-flex rounded border border-slate-800 p-0.5 text-[11px] font-normal" data-testid="qc-operator-range">
          {[["day", "Hari ini"], ["week", "7 hari"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setRange(v)} className={`rounded px-2 py-0.5 transition-colors ${range === v ? "bg-sky-500/20 text-sky-300" : "text-slate-500 hover:text-slate-300"}`} data-testid={`qc-operator-range-${v}`}>{l}</button>
          ))}
        </div>
        <span className="font-mono text-xs text-slate-500" data-testid="qc-operator-total">{stats ? `${range === "week" ? `${stats.days?.[0]} → ${stats.date}` : stats.date} · ${stats.total} scan` : ""}</span>
      </h3>
      {!stats ? <Loading /> : !stats.operators.length ? (
        <p className="text-xs text-slate-500" data-testid="qc-operator-empty">Belum ada scan pada periode ini.</p>
      ) : range === "week" ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="qc-operator-week-table">
            <thead>
              <tr className="text-[10px] text-slate-500">
                <th className="py-1 text-left font-normal">Operator</th>
                {stats.days.map((d) => <th key={d} className="py-1 text-center font-normal">{dayLabel(d)}</th>)}
                <th className="py-1 text-right font-normal">Total</th>
              </tr>
            </thead>
            <tbody>
              {stats.operators.map((o) => (
                <tr key={o.operator} className="border-t border-slate-800/60" data-testid={`qc-operator-week-row-${o.operator.replace(/\s+/g, "-")}`}>
                  <td className="max-w-[9rem] truncate py-1.5 text-slate-200">{o.operator}</td>
                  {o.daily.map((n, i) => (
                    <td key={stats.days[i]} className="py-1.5 text-center font-mono">
                      <span className={`inline-block min-w-[2rem] rounded px-1 ${n ? "text-slate-100" : "text-slate-600"}`} style={n ? { backgroundColor: `rgba(14,165,233,${0.15 + 0.55 * (n / dayMax)})` } : undefined}>{n || "·"}</span>
                    </td>
                  ))}
                  <td className="py-1.5 text-right font-mono"><span className="text-slate-100">{o.total}</span> <span className="text-emerald-400">{o.ok}</span>/<span className="text-amber-400">{o.repair}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <ul className="space-y-2">
          {stats.operators.map((o) => (
            <li key={o.operator} className="text-xs" data-testid={`qc-operator-row-${o.operator.replace(/\s+/g, "-")}`}>
              <div className="flex items-center gap-2">
                <span className="w-40 truncate text-slate-200">{o.operator}</span>
                <div className="h-2 flex-1 overflow-hidden rounded bg-slate-800">
                  <div className="flex h-full">
                    <div className="h-full bg-emerald-500" style={{ width: `${(o.ok / max) * 100}%` }} />
                    <div className="h-full bg-amber-500" style={{ width: `${(o.repair / max) * 100}%` }} />
                  </div>
                </div>
                <span className="w-28 text-right font-mono"><span className="text-slate-100">{o.total}</span> <span className="text-emerald-400">{o.ok} OK</span> <span className="text-amber-400">{o.repair} R</span></span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
